// КЛИЕНТ `/api/transcribe` (сервер — Г-3, контракт в `api/transcribe.js`). Г-4.
//
// ⭐ ПОВТОРЫ — ОДИН СЛОЙ, И ЭТО БРАУЗЕР (`s995.d1`, тикет `3c219875cbb881cc9f31d19a4fb24db7`; сервер их не
// делает — Г-3 п. 2). Кусок (`chunkWithRetry`): попытка ≤ 4 с, до 2 попыток, пауза 0,5 с — худший случай 8,5 с,
// меньше 10 с куска. Финал (`transcribe` с `kind: 'final'`): одна попытка, 10 с + 2 с на минуту записи, без повтора.
// Повторяется только то, что может пройти со второго раза: таймаут, обрыв сети, 5xx. Лимит (`s994.d1`) и прочие
// 4xx — нет.
//
// Исходы:
//   { ok: true, text, duration, remaining }  — расшифровка;
//   { limit: true }                          — `403 voice_limit` (`s994.d1`): гасить микрофон, поле живое;
//   { failed: true, retry: boolean }         — всё остальное; `retry` — стоит ли пробовать снова.
//
// ⚠️ ТЕЛО — `application/octet-stream`, тип звука — параметром `type`: `@vercel/node` отдаёт функции
// тело только для этого типа (замер Г-3).

export const TRANSCRIBE_URL = '/api/transcribe';

/** Сколько браузер ждёт одну попытку куска (`s995.d1`; сервер бросает провайдера на 3,5 с). */
export const CHUNK_WAIT_MS = 4_000;
/** Попыток куска и пауза между ними (`s995.d1`): худший случай 4 + 0,5 + 4 = 8,5 с < 10 с куска. */
export const CHUNK_ATTEMPTS = 2;
export const CHUNK_RETRY_PAUSE_MS = 500;

/** Потолок одной записи поля — 5 мин (решение Кирилла 19.10, строка 85; было 3 мин `s908.d3`). Бюджет прохода не меняется. */
export const MAX_RECORDING_S = 300;

/** Сколько браузер ждёт финал: 10 с + 2 с на минуту записи, одна попытка (`s995.d1`, Г-2); 5 мин → 20 с. */
export function finalWaitMs(seconds) {
  const s = Math.min(MAX_RECORDING_S, Math.max(0, Number(seconds) || 0));
  return 10_000 + 2_000 * (s / 60);
}

/**
 * @param {{audio: Blob|Uint8Array, mimeType: string, kind: 'chunk'|'final', pass: string, stages: number,
 *          seconds?: number, level?: {peak: number, p90: number}|null}} req
 * @param {{fetchImpl?: Function, url?: string}} [deps]
 */
export async function transcribe({ audio, mimeType, kind, pass, stages, seconds, level }, { fetchImpl = globalThis.fetch, url = TRANSCRIBE_URL } = {}) {
  const type = String(mimeType || 'audio/mp4').split(';')[0].trim();
  const q = new URLSearchParams({ kind, pass, stages: String(stages), type });
  if (kind === 'final' && Number.isFinite(seconds)) q.set('dur', String(Math.round(seconds)));
  // Уровень записи — только с финалом, два числа (строка 81): сервер пишет их в лог, доля тихих голосов в бете.
  if (kind === 'final' && level && Number.isFinite(level.peak) && Number.isFinite(level.p90)) q.set('lvl', `${level.peak},${level.p90}`);
  const waitMs = kind === 'chunk' ? CHUNK_WAIT_MS : finalWaitMs(seconds);

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), waitMs);
  try {
    const resp = await fetchImpl(`${url}?${q}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: audio,
      signal: ac.signal,
    });
    let body = null;
    try { body = await resp.json(); } catch { body = null; }
    if (resp.status === 403 && body?.error === 'voice_limit') return { limit: true };
    if (resp.status >= 400 && resp.status < 500) return { failed: true, retry: false };
    if (!resp.ok || typeof body?.text !== 'string') return { failed: true, retry: true };
    return { ok: true, text: body.text, duration: Number(body.duration) || 0, remaining: Number(body.remaining_s) };
  } catch {
    // таймаут (`abort`) или обрыв сети
    return { failed: true, retry: true };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Кусок с молчаливыми повторами (`s995.d1`). Индикатор записи при этом не меняется — повторы невидимы
 * (`s517.d7`). Исчерпал попытки — `{ failed: true, exhausted: true }`: дыра в черновике, её закроет финал.
 * @param {object} req — как у `transcribe`, `kind` ставится сам
 * @param {{fetchImpl?: Function, url?: string, sleep?: (ms: number) => Promise<void>, transcribe?: Function}} [deps]
 */
export async function chunkWithRetry(req, deps = {}) {
  const sleep = deps.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  const call = deps.transcribe ?? transcribe;
  let r = null;
  for (let attempt = 1; attempt <= CHUNK_ATTEMPTS; attempt++) {
    if (attempt > 1) await sleep(CHUNK_RETRY_PAUSE_MS);
    r = await call({ ...req, kind: 'chunk' }, deps);
    if (!r.failed || !r.retry) return r;
  }
  return { ...r, exhausted: true };
}

/**
 * Id ПРОХОДА визарда для лимита (`s994.d4`, Г-3 п. 5): живёт в браузере ВНЕ конверта сессии
 * (`s929.d2`, подъёма версии нет). `localStorage` — чтобы перезагрузка не давала новый бюджет;
 * недоступен — id на время жизни страницы.
 */
const PASS_KEY = 'lm.voice.pass';
let memoryPass = null;

export function voicePass(storage = typeof window === 'undefined' ? null : window.localStorage) {
  const fresh = () => {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return `p_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  };
  try {
    const have = storage?.getItem(PASS_KEY);
    if (have && /^[A-Za-z0-9_-]{16,64}$/.test(have)) return have;
    const id = fresh();
    storage?.setItem(PASS_KEY, id);
    if (storage) return id;
  } catch { /* приватный режим и т. п. */ }
  memoryPass = memoryPass || fresh();
  return memoryPass;
}
